// moduleShellComposer.js — a module page hides the shell's global composer
// ("Ask anything…", the root CanvasPromptTextarea) while it shows a composer
// of its own (MODULE-SHELL-COMPOSER-1).
//
//   el.call('setShellComposer', false)   // hide it while THIS page shows its own
//   el.call('setShellComposer', true)    // show it again
//
// Before this seam the only module path to the composer was immersive chrome
// (manifest `chrome.immersive`), which also hides the shell header and holds
// for the whole module surface. A module that opens its own input panel (the
// VaultOS Adjust panel) needs the narrow half only: no second prompt under its
// own, the header untouched, and nothing changed anywhere else.
//
// WHO MAY ASK — only a module's OWN PAGE, resolved from the caller's MOUNT
// POINT, never from its context alone. A module's context also reaches its
// rail (mounted on every route, `/` included) and its home widget bodies
// (mounted inside the shell's home); a context-only check let those hide the
// composer on `/` and on another module's page (ws crm review, 2026-10-09).
// The rule (`moduleOwnPageOf`): walk up from the caller to the topmost element
// that still carries the caller's module context — the module's mount root.
// It must be the CONTENT child of either
//   • a PageSlot pane host (`_slotPath` stamp; a family host's `Body` too) —
//     the page the pane shows, or
//   • the routed shell root (the element carrying `.routes`) — the page the
//     router mounted for the main route.
// The router and PageSlot mount a page def with its own module context, so a
// content child carrying module M's context IS one of M's own pages. Anything
// else — the rail, a widget body, a component nested in a shell surface — is
// refused.
//
// THE LATCH — `root._moduleComposerHidden`, ONE SLOT PER SURFACE:
//   { main?, _canvasSlot?, _canvasSlotSecond?: { moduleId, route, token } }
// A pane page and the main page each own their slot: a pane never overwrites
// the main page's latch, and closing the pane never un-hides the main page.
// `route` is the page's path (query/hash dropped, `/w/<slug>` prefix
// stripped); `token` names the mounted page element in this module's registry
// (`_mounts`), so the latch also dies with the page itself.
//
// A SLOT IS LIVE only while (a) its page element is still mounted at the same
// mount point (not disposed, not replaced — a same-route re-mount or a failed
// mount counts as gone) and (b) its surface still shows that route. The
// composer hides while ANY slot is live (`moduleComposerHiddenLive`, read by
// app.js _composerVisible). `releaseStaleModuleComposerLatch` (the shell's
// route sync — subscribed to currentPath and both pane keys) clears dead
// slots, so coming back to a page shows the composer until the module asks
// again. A lifecycle error inside the latched page releases its slot at once
// (`buildModuleComposerErrorPlugin`, a module-context plugin). A workspace
// switch wipes the key (state-app.js WORKSPACE_SCOPED_KEYS).
//
// GAP (framework): DOMQL has no public "element disposed" subscription, so an
// unmount that writes no shell state at all is seen at the next evaluation of
// the composer / route sync, not at the instant of disposal.
import { shellStripWorkspacePrefix } from './shellSharedDelegates.js'

const PANE_KEYS = new Set(['_canvasSlot', '_canvasSlotSecond'])
const SLOTS = ['main', '_canvasSlot', '_canvasSlotSecond']

// token → { top, parent, ck } — the mounted page each latch slot names.
// Session-only, like the latch itself.
const _mounts = new Map()
let _seq = 0

// Path only — no query, no hash, no `/w/<slug>` prefix, never empty.
const _pathOf = (raw) => {
  const s = String(raw || '')
    .split('#')[0]
    .split('?')[0]
  return s || '/'
}

const _mainRoute = (root) =>
  _pathOf(shellStripWorkspacePrefix(_pathOf(root && root.currentPath ? root.currentPath : '/')))

const _contentKeyOf = (parent) =>
  (parent && parent.__ref && parent.__ref.contentElementKey) || 'content'

const _slotHost = (parent) => {
  if (!parent) return null
  if (typeof parent._slotPath === 'string' && parent._slotPath) return parent
  const up = parent.parent
  if (parent.key === 'Body' && up && typeof up._slotPath === 'string' && up._slotPath) return up
  return null
}

// The module page the caller is mounted in, or null when the caller is not
// inside one of its module's own pages (see header). Returns
// { top, parent, ck, slot, route? } — `route` for a pane (its own route).
export const moduleOwnPageOf = (el) => {
  const ctx = el && el.context
  if (!ctx || typeof ctx !== 'object') return null
  let top = el
  let hops = 0
  while (top.parent && top.parent !== top && top.parent.context === ctx && hops < 1000) {
    top = top.parent
    hops++
  }
  const parent = top.parent
  if (!parent || parent === top) return null
  const ck = _contentKeyOf(parent)
  if (top.key !== ck || parent[ck] !== top) return null
  const host = _slotHost(parent)
  if (host) {
    const slot = PANE_KEYS.has(host._slotPaneKey) ? host._slotPaneKey : '_canvasSlot'
    return { top, parent, ck, slot, route: _pathOf(host._slotPath) }
  }
  if (parent.routes && typeof parent.routes === 'object') return { top, parent, ck, slot: 'main' }
  return null
}

const _mountAlive = (token) => {
  const m = token ? _mounts.get(token) : null
  return !!(m && m.top && m.top.__ref && m.parent && m.parent[m.ck] === m.top)
}

const _slotLive = (root, slot, latch) => {
  if (!latch || typeof latch !== 'object' || !latch.moduleId || !latch.route) return false
  if (!_mountAlive(latch.token)) return false
  if (slot === 'main') return _mainRoute(root) === latch.route
  if (!PANE_KEYS.has(slot)) return false
  const live = root[slot]
  return typeof live === 'string' && !!live && _pathOf(live) === latch.route
}

const _latchOf = (root) => {
  const v = root ? root._moduleComposerHidden : null
  return v && typeof v === 'object' ? v : null
}

// Is any module page's "hide the shell composer" latch live right now?
export const moduleComposerHiddenLive = (root) => {
  const latch = _latchOf(root)
  if (!latch) return false
  return SLOTS.some((slot) => _slotLive(root, slot, latch[slot]))
}

// The latch with dead slots dropped (null when none is left), or `undefined`
// when nothing changes.
const _pruned = (root) => {
  const latch = _latchOf(root)
  if (!latch) return undefined
  const next = {}
  let changed = false
  for (const slot of Object.keys(latch)) {
    if (SLOTS.includes(slot) && _slotLive(root, slot, latch[slot])) next[slot] = latch[slot]
    else {
      changed = true
      if (latch[slot] && latch[slot].token) _mounts.delete(latch[slot].token)
    }
  }
  if (!changed) return undefined
  return Object.keys(next).length ? next : null
}

// Clear dead slots. Returns true when it wrote.
export const releaseStaleModuleComposerLatch = (root) => {
  if (!root || typeof root.update !== 'function') return false
  const next = _pruned(root)
  if (next === undefined) return false
  root.update({ _moduleComposerHidden: next }, { preventFetch: true })
  return true
}

const _writeSlot = (root, slot, value) => {
  const latch = _latchOf(root) || {}
  const prev = latch[slot]
  if (prev && prev.token && (!value || prev.token !== value.token)) _mounts.delete(prev.token)
  const next = { ...latch }
  if (value) next[slot] = value
  else delete next[slot]
  root.update(
    { _moduleComposerHidden: Object.keys(next).length ? next : null },
    { preventFetch: true }
  )
}

// The module-facing bridge. `moduleIdOf(context)` names the installed module
// that owns a scope context (workspaceProjects.js injects it); without it
// every call is refused. Returns true when the call took effect.
export const buildModuleShellComposerBridge = (root, { moduleIdOf } = {}) =>
  function setShellComposer(visible) {
    if (!root || typeof root.update !== 'function') return false
    if (visible !== true && visible !== false) {
      console.warn(
        `[setShellComposer] ignored "${String(visible)}" — pass false to hide the shell composer on this page, true to show it again`
      )
      return false
    }
    const moduleId = typeof moduleIdOf === 'function' ? moduleIdOf(this && this.context) : null
    if (!moduleId) {
      console.warn('[setShellComposer] refused — the caller is not inside an installed module')
      return false
    }
    const page = moduleOwnPageOf(this)
    if (!page) {
      console.warn(
        "[setShellComposer] refused — call it from the module's own page (a PageSlot pane or the routed page), not from its rail or a home widget"
      )
      return false
    }
    const route = page.slot === 'main' ? _mainRoute(root) : page.route
    const current = (_latchOf(root) || {})[page.slot]
    if (visible) {
      if (!current) return true
      // One module never clears another module's latch.
      if (current.moduleId !== moduleId) return false
      _writeSlot(root, page.slot, null)
      return true
    }
    if (
      current &&
      current.moduleId === moduleId &&
      current.route === route &&
      _mountAlive(current.token) &&
      _mounts.get(current.token).top === page.top
    ) {
      return true
    }
    _seq += 1
    const token = 'c' + _seq
    _mounts.set(token, { top: page.top, parent: page.parent, ck: page.ck })
    _writeSlot(root, page.slot, { moduleId, route, token })
    return true
  }

// A module-context plugin: a lifecycle error inside a latched page releases
// that page's slot at once (the route may stay). DOMQL treats a plugin `error`
// hook as HANDLING the error and then skips its own console.error, so this
// hook logs it in the framework's own format whenever no OTHER plugin handles
// errors — attaching it never silences a module's errors.
export const buildModuleComposerErrorPlugin = (root) => {
  const plugin = {
    error(el, info) {
      try {
        const latch = _latchOf(root)
        if (latch && typeof root.update === 'function') {
          for (const slot of SLOTS) {
            const l = latch[slot]
            const m = l && l.token ? _mounts.get(l.token) : null
            if (!m) continue
            let node = el
            let hops = 0
            while (node && node !== m.top && hops < 1000) {
              node = node.parent === node ? null : node.parent
              hops++
            }
            if (node === m.top) _writeSlot(root, slot, null)
          }
        }
      } catch (_) {
        /* never let the latch turn an error into a second error */
      }
      const plugins = (el && el.context && el.context.plugins) || []
      const others = plugins.some((p) => p && p !== plugin && typeof p.error === 'function')
      if (!others) {
        console.error(`[DOMQL] Lifecycle "${info && info.hook}" error:`, info && info.error)
      }
    }
  }
  // Marker AND the root it is bound to (workspaceProjects.js re-binds on a
  // replaced root).
  plugin.__moduleComposerLatch = root
  return plugin
}
